import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Camera } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { TextAreaInput } from '@liveauctioneers/hammer-ui-core/textArea';
import { take } from 'es-toolkit';
import { memo, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { required } from '@/utils/validation';

const MAX_IMAGE_UPLOAD_COUNT = 5;

type Props = {
    isMobile: boolean;
    messageDetails: any;
    sellerId: number;
    subject: string;
    submitConsignment: Function;
    uiError: boolean;
    uiSubmitted: boolean;
};

const SendSellerConsignmentMessageModalDetailsComponent = (props: Props) => {
    const formatMessage = useAppFormatMessage();
    const { isMobile = false, sellerId = 0, subject, submitConsignment, uiError, uiSubmitted } = props;

    const [imageUploads, setImageUploads] = useState([]);
    const [message, setMessage] = useState('');
    const [messageValid, setMessageValid] = useState(true);
    const [photoErrors, setPhotoErrors] = useState([]);
    const [photoValid, setPhotoValid] = useState(true);

    const onValueChange = ({ target }: any) => {
        const { id, value } = target;

        if (id === 'message') {
            setMessage(value);
        }
    };

    const onSubmit = useCallback(() => {
        const valid = required(message) && message.length >= 30;
        setMessageValid(valid);
        setPhotoErrors([]);
        setPhotoValid(true);

        if (valid) {
            submitConsignment({
                attachments: imageUploads,
                messageBody: message,
                sellerId,
                subject,
            });
        }
    }, [imageUploads, message, sellerId, subject, submitConsignment]);

    const onDrop = useCallback(
        (accepted: any, rejected: any) => {
            const photoErrors = [];
            const acceptedWithPreview = accepted.map((file) =>
                Object.assign(file, { preview: URL.createObjectURL(file) })
            );
            const attachments = imageUploads.concat(acceptedWithPreview);

            if (rejected.length > 0) {
                photoErrors.push('image_not_correct_format');
            }

            if (attachments.length > MAX_IMAGE_UPLOAD_COUNT) {
                photoErrors.push('only_five_images_allowed');
            }

            setImageUploads(take(attachments, MAX_IMAGE_UPLOAD_COUNT));
            setPhotoErrors(photoErrors);
            setPhotoValid(photoErrors.length === 0);
        },
        [imageUploads]
    );
    const disabled = imageUploads.length >= 5;

    // Create a dropzone for file uploads
    const {
        getInputProps,
        getRootProps,
        open: openFileDialog,
    } = useDropzone({
        // Accept all images
        accept: 'image/*',
        disabled,
        // Maximum size (10MB) per file
        maxSize: 10e6,
        noClick: true,
        onDrop,
    });

    const onDropLinkClick = () => {
        if (imageUploads.length >= 5) {
            setPhotoErrors(['only_five_images_allowed']);
            setPhotoValid(false);
        } else {
            openFileDialog();
        }
    };

    let msgErr = null;
    if (!messageValid) {
        msgErr = formatMessage(['item_desc_too_short']);
    }

    if (uiError) {
        msgErr = formatMessage(['looks_like_something_went_wrong']);
    }

    const clearAttachments = useCallback(() => {
        setImageUploads([]);
        setPhotoErrors([]);
        setPhotoValid(true);
    }, []);

    return (
        <BodyPrimary className="max-xsMax:px-[30px] max-xsMax:pb-[65px] max-xsMax:pt-[40px] flex flex-col items-center overflow-y-auto px-2xl py-[40px] text-left text-[11px] tracking-[1px]">
            <form
                className="w-full font-normal"
                onSubmit={onSubmit}
            >
                <div className="min-h-[152px]">
                    <label className="!w-full pr-2xs font-normal uppercase leading-[1.45] text-text-secondary">
                        {formatMessage(['tell_us_about_your_thing'])}
                    </label>
                    <label className="!w-full font-normal uppercase leading-[1.45] text-text-secondary">
                        {formatMessage(['consignment_item_description'])}
                    </label>
                    <TextAreaInput
                        aria-label={String(formatMessage(['consignment_item_description']))}
                        className="[&_textarea]:max-h-[330px] [&_textarea]:min-h-[80px] [&_textarea]:resize-y"
                        data-testid="consignment-text-input"
                        errorText={
                            msgErr
                                ? String(formatMessage(['item_desc_too_short']))
                                : String(formatMessage(['looks_like_something_went_wrong']))
                        }
                        hasError={!messageValid || uiError}
                        id="message"
                        labelText={String(formatMessage(['consignment_item_description']))}
                        onChange={onValueChange}
                    />
                </div>

                <div className="mt-sm min-h-[260px]">
                    <label
                        className="!w-full font-normal uppercase leading-[1.45] text-text-secondary"
                        htmlFor="file-uploader"
                    >
                        {formatMessage(['add_photos'])}
                    </label>
                    &nbsp;
                    <label className="!w-full font-normal uppercase leading-[1.45] text-text-secondary">
                        {formatMessage(['add_photos_description'])}
                    </label>
                    <div
                        className="min-h-[157px] border border-solid border-border-secondary"
                        data-testid="image-dropzone"
                        {...getRootProps()}
                    >
                        <input
                            {...getInputProps()}
                            data-testid="image-uploader"
                            id="file-uploader"
                        />
                        <div className="flex min-h-[155px] flex-col items-center justify-center p-xs">
                            {!isMobile && (
                                <Camera
                                    className="text-surface-500"
                                    style="fill"
                                />
                            )}
                            <div style={{ display: 'inline' }}>
                                {!isMobile && <span>Drag or&nbsp;</span>}
                                <Link onClick={onDropLinkClick}>{formatMessage(['click_to_upload'])}</Link>
                            </div>
                            <div className="mt-xs text-center [&_img]:max-h-[52px] [&_img]:max-w-[85px] [&_img]:px-[2.5px] [&_img]:py-[5px]">
                                {imageUploads.map((f) => (
                                    <img
                                        alt={f.name}
                                        key={f.name}
                                        src={f.preview}
                                    />
                                ))}
                            </div>
                            {imageUploads.length > 0 && (
                                <Link onClick={clearAttachments}>
                                    <span>Clear</span>
                                </Link>
                            )}
                        </div>
                    </div>
                    <div>
                        {!photoValid &&
                            photoErrors.map((e, idx) => (
                                <div
                                    className="!text-text-critical"
                                    key={idx}
                                >
                                    <FormattedMessage id={e} />
                                </div>
                            ))}
                    </div>
                </div>
            </form>
            <div className="w-full text-center">
                <Button
                    className="max-xsMax:w-full w-[264px]"
                    data-testid="sendSellerConsignmentMessageModalSubmit"
                    disabled={uiSubmitted}
                    loading={uiSubmitted}
                    onClick={onSubmit}
                >
                    {formatMessage(['submit'])}
                </Button>
            </div>
        </BodyPrimary>
    );
};

export const SendSellerConsignmentMessageModalDetails = memo<Props>(SendSellerConsignmentMessageModalDetailsComponent);
